import { ScheduleGridHeaderCellProps } from "components/ScheduleGrid/ScheduleGrid.Types";
import { renderDate } from "@intrasync/utils";
import { injectStores } from "@intrasync/react";
import type { RouterStore } from "@intrasync/stores";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import ProductionOverviewScheduleStore from "../ProductionOverviewScheduleStore";

interface ProductionOverviewRightHeaderCellProps
  extends ScheduleGridHeaderCellProps {
  currentHeader: {
    title: string;
    date?: string;
    key: string;
  };
  routerStore?: RouterStore;
  productionOverviewScheduleStore?: ProductionOverviewScheduleStore;
}

const ProductionOverviewRightHeaderCellImpl = (
  props: ProductionOverviewRightHeaderCellProps
) => {
  const { currentHeader, routerStore, productionOverviewScheduleStore } = props;
  const date = currentHeader?.date || currentHeader?.key || currentHeader?.title;

  // Get capacity info for this date
  const capacityInfo = productionOverviewScheduleStore?.getCapacityForDate(date) || {
    capacity: 0,
    workerCount: null,
    hoursPerWorker: null,
  };
  const scheduledManHours = productionOverviewScheduleStore?.getScheduledManHoursForDate(date) || 0;

  const capacityManHours = capacityInfo.capacity;
  const remainingCapacity = capacityManHours - scheduledManHours;
  const isOverCapacity = capacityManHours > 0 && scheduledManHours > capacityManHours;
  const hasCapacity = capacityManHours > 0;

  const handleOpenCapacityDialog = (e: React.MouseEvent) => {
    e.stopPropagation();
    routerStore?.openDialog({
      dialogName: "setCapacity",
      dialogContext: date,
    });
  };

  return (
    <div
      style={{
        border: "1px solid #eee",
        ...props.style,
        ...props.styles,
        display: "flex",
        flexDirection: "column",
        padding: "4px 8px",
      }}
    >
      {/* Date header with settings icon */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {renderDate(date)}
        </Typography>
        <Tooltip title="Set capacity">
          <IconButton
            size="small"
            onClick={handleOpenCapacityDialog}
            sx={{ padding: "2px" }}
          >
            <SettingsIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Capacity display */}
      <Box sx={{ borderTop: "1px dashed #ccc", pt: "2px" }}>
        {hasCapacity ? (
          <>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: isOverCapacity ? "error.main" : "success.main",
                fontWeight: "bold",
                fontSize: "11px",
              }}
            >
              {scheduledManHours.toFixed(1)} / {capacityManHours.toFixed(0)} hrs
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: isOverCapacity ? "error.main" : "text.secondary",
                fontSize: "10px",
              }}
            >
              {isOverCapacity
                ? `Over ${Math.abs(remainingCapacity).toFixed(1)}`
                : `${remainingCapacity.toFixed(1)} left`}
            </Typography>
          </>
        ) : (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "warning.main",
              cursor: "pointer",
              fontSize: "10px",
            }}
            onClick={handleOpenCapacityDialog}
          >
            Set capacity
          </Typography>
        )}
      </Box>
    </div>
  );
};

const ProductionOverviewRightHeaderCell = injectStores(ProductionOverviewRightHeaderCellImpl);

export default ProductionOverviewRightHeaderCell;
